// Device-side API client for the P1-03 catalog viewer. Reads the
// signed-in JWT from sessionStorage (same storage key as the audit
// viewer) and GETs /catalog/items + /catalog/items/:id. Barcode lookup
// is exposed for the future scanner path even though the P1-03 UI
// doesn't call it yet.

import { readSessionToken } from "./audit.ts";
import type { AmmoAttributes, HolsterAttributes, OpticsAttributes } from "./catalog-attrs.ts";

export { readSessionToken };

const DEFAULT_API_URL = "http://localhost:3001";

export function apiBase(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) ?? DEFAULT_API_URL;
}

export type ItemSummary = {
  id: string;
  sku: string;
  displayName: string;
  kind: string;
  defaultPriceCents: string;
  offlineSellable: boolean;
  restrictedFlag: boolean;
  minAge: number | null;
  categoryId: string | null;
  brandId: string | null;
  archived: boolean;
};

export type ItemDetail = {
  item: ItemSummary & {
    tagLine: string | null;
    tagNameOverride: string | null;
    description: string | null;
    restrictedReason: string | null;
    msrpCents: string | null;
    mapCents: string | null;
    reorderMin: number | null;
    reorderMax: number | null;
    requiresLock: boolean;
    archivedAt: string | null;
    taxCategoryId: string;
    unitId: string | null;
    // Returned by the API so PATCH /items/:id can send it back as
    // expected_version (optimistic concurrency control, P1-18 Phase 2).
    version: number;
  };
  brand: { code: string; displayName: string } | null;
  category: { code: string; displayName: string } | null;
  unit: { code: string; displayName: string } | null;
  taxCategory: { code: string; displayName: string; kind: string } | null;
  firearmAttributes: FirearmAttributes | null;
  ammoAttributes: AmmoAttributes | null;
  opticsAttributes: OpticsAttributes | null;
  holsterAttributes: HolsterAttributes | null;
  photos: Photo[];
  barcodes: Array<{ id: string; code: string; packQty: number; isPrimary: boolean }>;
  variants: Array<{
    id: string;
    variantSku: string;
    displayName: string;
    dim1: { name: string; value: string } | null;
    dim2: { name: string; value: string } | null;
    dim3: { name: string; value: string } | null;
    priceOverrideCents: string | null;
  }>;
  prices: Array<{
    id: string;
    priceCents: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    notes: string | null;
    priceLevel: { code: string; displayName: string };
  }>;
};

export type Photo = {
  id: string;
  storageKey: string;
  mimeType: string;
  sizeBytes: number;
  widthPx: number | null;
  heightPx: number | null;
  sortOrder: number;
  caption: string | null;
  // null when storage was unreachable at response time; UI shows a broken-
  // image placeholder but still lets the user delete the row.
  signedUrl: string | null;
  expiresAt: string | null;
};

export type FirearmAttributes = {
  manufacturer: string;
  importer: string | null;
  model: string;
  atfType: string;
  mnCategory: string;
  actionType: string | null;
  caliberGauge: string;
  conditionNewUsed: "new" | "used";
  barrelLengthIn: string | null;
  overallLengthIn: string | null;
  magazineCapacity: number | null;
  includedMagazines: string | null;
  finish: string | null;
  handedness: "right" | "left" | "ambidextrous" | null;
  countryOfOrigin: string | null;
  nfaType: string | null;
  isAntique: boolean;
  isPrivatelyMade: boolean;
  // Short descriptors (e.g. "Optic Ready", "Threaded Barrel") appended to the
  // auto-generated tag name. Column lands with the schema PR; API currently
  // omits so defaults to [].
  features?: string[] | null;
  // Server-side version used by PATCH /items/:id/firearm as
  // expected_version (optimistic concurrency control, P1-18 Phase 2).
  version: number;
};

// Per-kind attribute types (P1-18 PR A) live in ./catalog-attrs.ts to keep
// this file under the 400-line guard. Re-export so existing imports keep
// working with a single path.
export type {
  AmmoAttributes,
  AmmoBulletType,
  HolsterAttributes,
  HolsterDrawSide,
  HolsterMaterial,
  HolsterMountType,
  OpticsAttributes,
} from "./catalog-attrs.ts";

export type SearchFilters = {
  q?: string | undefined;
  kind?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
};

export type SearchResult =
  | { ok: true; items: ItemSummary[]; limit: number; offset: number; hasMore: boolean }
  | { ok: false; status: number; error: string };

export type DetailResult =
  | { ok: true; detail: ItemDetail }
  | { ok: false; status: number; error: string };

export type BarcodeResult =
  | { ok: true; item: ItemSummary & { restrictedReason: string | null }; packQty: number }
  | { ok: false; status: number; error: string };

export async function searchCatalog(
  token: string,
  filters: SearchFilters = {},
): Promise<SearchResult> {
  const url = new URL(`${apiBase()}/catalog/items`);
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && String(v).length > 0) {
      url.searchParams.set(k, String(v));
    }
  }
  const res = await fetch(url.toString(), {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) return decodeError(res);
  const body = (await res.json()) as {
    items: ItemSummary[];
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  return { ok: true, ...body };
}

export async function getCatalogItem(token: string, id: string): Promise<DetailResult> {
  const res = await fetch(`${apiBase()}/catalog/items/${encodeURIComponent(id)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) return decodeError(res);
  const body = (await res.json()) as ItemDetail;
  return { ok: true, detail: body };
}

export async function getCatalogByBarcode(token: string, code: string): Promise<BarcodeResult> {
  const res = await fetch(`${apiBase()}/catalog/barcodes/${encodeURIComponent(code)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) return decodeError(res);
  const body = (await res.json()) as {
    item: ItemSummary & { restrictedReason: string | null };
    packQty: number;
  };
  return { ok: true, item: body.item, packQty: body.packQty };
}

async function decodeError(res: Response): Promise<{ ok: false; status: number; error: string }> {
  const body = (await res.json().catch(() => ({ error: "unknown" }))) as { error?: string };
  return { ok: false, status: res.status, error: body.error ?? "unknown" };
}

// --- Item photos ----------------------------------------------------------

export type UploadPhotoResult =
  | { ok: true; photo: Photo }
  | { ok: false; status: number; error: string; stage: "sign" | "put" | "create" };

export type DeletePhotoResult = { ok: true } | { ok: false; status: number; error: string };

export type ReorderPhotosResult = { ok: true } | { ok: false; status: number; error: string };

// Three-step orchestration: sign → PUT → create. Returns the created
// metadata on success, or a stage-tagged error so the caller can surface
// a precise message. No optimistic insert here — the caller (UI) is
// responsible for the local list state; this function only talks to the
// server. On any failure the caller must roll back its optimistic row.
export async function uploadPhoto(
  token: string,
  itemId: string,
  file: Blob,
): Promise<UploadPhotoResult> {
  // Step 1: sign.
  let signRes: Response;
  try {
    signRes = await fetch(
      `${apiBase()}/catalog/items/${encodeURIComponent(itemId)}/photos:signed-url`,
      {
        method: "POST",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ mimeType: file.type, sizeBytes: file.size }),
      },
    );
  } catch {
    return { ok: false, status: 0, error: "network_error", stage: "sign" };
  }
  if (!signRes.ok) {
    const b = (await signRes.json().catch(() => ({ error: "unknown" }))) as { error?: string };
    return { ok: false, status: signRes.status, error: b.error ?? "unknown", stage: "sign" };
  }
  const signed = (await signRes.json()) as {
    signedUrl: string;
    storageKey: string;
    photoId: string;
    expiresInSec: number;
  };

  // Step 2: PUT the file bytes to Storage.
  let putRes: Response;
  try {
    putRes = await fetch(signed.signedUrl, {
      method: "PUT",
      headers: { "content-type": file.type, "x-upsert": "false" },
      body: file,
    });
  } catch {
    return { ok: false, status: 0, error: "network_error", stage: "put" };
  }
  if (!putRes.ok) {
    return { ok: false, status: putRes.status, error: "storage_put_failed", stage: "put" };
  }

  // Step 3: tell the API to persist metadata.
  let createRes: Response;
  try {
    createRes = await fetch(`${apiBase()}/catalog/items/${encodeURIComponent(itemId)}/photos`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({
        photoId: signed.photoId,
        storageKey: signed.storageKey,
        mimeType: file.type,
        sizeBytes: file.size,
      }),
    });
  } catch {
    return { ok: false, status: 0, error: "network_error", stage: "create" };
  }
  if (!createRes.ok) {
    const b = (await createRes.json().catch(() => ({ error: "unknown" }))) as { error?: string };
    return {
      ok: false,
      status: createRes.status,
      error: b.error ?? "unknown",
      stage: "create",
    };
  }
  const body = (await createRes.json()) as { photo: Photo };
  return { ok: true, photo: body.photo };
}

export async function deletePhoto(token: string, photoId: string): Promise<DeletePhotoResult> {
  const res = await fetch(`${apiBase()}/catalog/photos/${encodeURIComponent(photoId)}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) return decodeError(res);
  return { ok: true };
}

// Edit mutations (P1-18 Phase 2) and the features-suggest read (P1-18
// Phase 3) live in ./catalog-edits.ts so this file stays under the
// 400-line guard. Re-export them here so consumers keep a single import
// path.
export type {
  AmmoAttributesUpdate,
  EditResult,
  FeatureSuggestion,
  FeatureSuggestionsResult,
  FirearmFeaturesUpdate,
  HolsterAttributesUpdate,
  OpticsAttributesUpdate,
  TagOverrideUpdate,
} from "./catalog-edits.ts";
export {
  fetchFeatureSuggestions,
  updateFirearmFeatures,
  updateItemAmmo,
  updateItemHolster,
  updateItemOptics,
  updateItemTagOverride,
} from "./catalog-edits.ts";

export async function reorderPhotos(
  token: string,
  itemId: string,
  orderedIds: string[],
): Promise<ReorderPhotosResult> {
  const res = await fetch(
    `${apiBase()}/catalog/items/${encodeURIComponent(itemId)}/photos:reorder`,
    {
      method: "PUT",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ orderedIds }),
    },
  );
  if (!res.ok) return decodeError(res);
  return { ok: true };
}

// Format cents as $1,234.56 for display. Keeps rounding at the display
// edge only; storage and arithmetic stay in integer cents.
export function formatCents(cents: string | number | bigint): string {
  const n = typeof cents === "string" ? Number(cents) : Number(cents);
  if (!Number.isFinite(n)) return "—";
  return (n / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export const ITEM_KINDS = [
  "standard",
  "serialized_firearm",
  "serialized_other",
  "service",
  "fee",
  "gift_card",
  "class_seat",
  "kit",
  "non_inventory",
] as const;

export function labelForKind(kind: string): string {
  switch (kind) {
    case "standard":
      return "Standard";
    case "serialized_firearm":
      return "Firearm";
    case "serialized_other":
      return "Serialized";
    case "service":
      return "Service";
    case "fee":
      return "Fee";
    case "gift_card":
      return "Gift card";
    case "class_seat":
      return "Class seat";
    case "kit":
      return "Kit";
    case "non_inventory":
      return "Non-inventory";
    default:
      return kind;
  }
}
